import React from 'react';
import { Box, Heading, Switch, Text, TextInput } from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import { EmployeeFieldFormData, EmployeeFieldValueType } from '../../../types';
import { FieldBehaviorRow } from '../../../styles';
import { FieldTypeSelector } from './FieldTypeSelector';
import { EnumValuesInput } from './EnumValuesInput';

export type FieldDetailsStepProps = {
  isTypeMenuOpen: boolean;
  onTypeMenuToggle: () => void;
  onTypeSelect: (valueType: EmployeeFieldValueType) => void;
  enumValueInput: string;
  onEnumValueInputChange: (value: string) => void;
  onEnumValueCommit: () => void;
  onEnumValueRemove: (value: string) => void;
};

export const FieldDetailsStep = ({
  isTypeMenuOpen,
  onTypeMenuToggle,
  onTypeSelect,
  enumValueInput,
  onEnumValueInputChange,
  onEnumValueCommit,
  onEnumValueRemove,
}: FieldDetailsStepProps) => {
  const { formData, register } = useFormWithProvider<EmployeeFieldFormData>();
  const valueType = formData.valueType ?? '';

  return (
    <Box padding={['spacing.8', 'spacing.7', 'spacing.6']} maxWidth="560px">
      <Heading size="large">Field details</Heading>
      <Text marginTop="spacing.2" color="surface.text.gray.muted">
        Define the field name and type
      </Text>
      <Box marginTop="spacing.7" display="flex" flexDirection="column" gap="spacing.5">
        <TextInput label="Field name" placeholder="e.g. Cost Centre" {...register('name')} />
        <FieldTypeSelector
          valueType={valueType}
          isOpen={isTypeMenuOpen}
          onToggle={onTypeMenuToggle}
          onSelect={onTypeSelect}
        />
        {valueType === 'ENUM' ? (
          <EnumValuesInput
            values={formData.enumValues ?? []}
            inputValue={enumValueInput}
            onInputChange={onEnumValueInputChange}
            onCommit={onEnumValueCommit}
            onRemove={onEnumValueRemove}
          />
        ) : null}
        <Box display="flex" flexDirection="column" gap="spacing.3">
          <Text weight="semibold">Field behaviour</Text>
          <Box
            borderWidth="thin"
            borderColor="surface.border.gray.subtle"
            borderRadius="large"
            overflow="hidden">
            <FieldBehaviorRow $isActive={Boolean(formData.isRequired)}>
              <Switch
                accessibilityLabel="Make this field mandatory"
                {...register('isRequired', 'switch')}
              />
              <Box>
                <Text weight="semibold">Make this field mandatory</Text>
                <Text size="small" color="surface.text.gray.muted">
                  Employees must fill this field before their profile is complete.
                </Text>
              </Box>
            </FieldBehaviorRow>
            <FieldBehaviorRow $isActive={Boolean(formData.showOnPayslip)}>
              <Switch
                accessibilityLabel="Show on payslip"
                {...register('showOnPayslip', 'switch')}
              />
              <Box>
                <Text weight="semibold">Show on payslip</Text>
                <Text size="small" color="surface.text.gray.muted">
                  Display this field in employee payslips.
                </Text>
              </Box>
            </FieldBehaviorRow>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
